"use client";

import { useEffect, useState } from "react";

type Lang = "de" | "en";

type ProjectListItem = {
  id: number;
  createdAt: string;
  title: string;
  status: string;
  versionNo: number;
  provider: string;
  executiveSummary: string;
};

const LANG_KEY = "vtob_lang";

const copy = {
  de: {
    title: "Projektarchiv",
    desc: "Hier findest du die zuletzt generierten Businessplan-Projekte.",
    refresh: "Aktualisieren",
    empty: "Noch keine Projekte vorhanden.",
    source: "Quelle",
    version: "Version",
    status: "Status",
    open: "Projekt oeffnen",
    created: "Erstellt",
    search: "Suche",
    searchPh: "Titel oder Zusammenfassung...",
    filter: "Filter",
    all: "Alle",
    generated: "Generiert",
    archived: "Archiviert",
    archive: "Archivieren",
    unarchive: "Reaktivieren",
    remove: "Loeschen",
    load: "Lade...",
    confirmDelete: "Projekt wirklich loeschen?",
    statusGenerated: "Generiert",
    statusArchived: "Archiviert",
    statusDraft: "Entwurf",
  },
  en: {
    title: "Project archive",
    desc: "Here you can find recently generated business plan projects.",
    refresh: "Refresh",
    empty: "No projects available yet.",
    source: "Source",
    version: "Version",
    status: "Status",
    open: "Open project",
    created: "Created",
    search: "Search",
    searchPh: "Title or summary...",
    filter: "Filter",
    all: "All",
    generated: "Generated",
    archived: "Archived",
    archive: "Archive",
    unarchive: "Reactivate",
    remove: "Delete",
    load: "Loading...",
    confirmDelete: "Delete this project?",
    statusGenerated: "Generated",
    statusArchived: "Archived",
    statusDraft: "Draft",
  },
} as const;

function getLang(): Lang {
  if (typeof window === "undefined") return "de";
  return window.localStorage.getItem(LANG_KEY) === "en" ? "en" : "de";
}

function formatDate(iso: string, lang: Lang) {
  const locale = lang === "de" ? "de-DE" : "en-US";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString(locale);
}

export default function ProjektePage() {
  const [lang, setLang] = useState<Lang>("de");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "generated" | "archived">("all");
  const [busyId, setBusyId] = useState<number | null>(null);

  async function loadProjects() {
    setLoading(true);
    setError("");
    try {
      const response = await fetch("/api/plan/projects", { cache: "no-store" });
      const data = await response.json();
      if (!response.ok || !data.ok) {
        throw new Error(data.error || "Fehler beim Laden");
      }
      setProjects(Array.isArray(data.projects) ? data.projects : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unbekannter Fehler");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const current = getLang();
    setLang(current);
    loadProjects();

    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent).detail;
      setLang(detail === "en" ? "en" : "de");
    };
    window.addEventListener("vtob-lang-change", handler);
    return () => window.removeEventListener("vtob-lang-change", handler);
  }, []);

  const t = copy[lang];
  const query = search.trim().toLowerCase();
  const statusLabel: Record<string, string> = {
    generated: t.statusGenerated,
    archived: t.statusArchived,
    draft: t.statusDraft,
  };

  const visibleProjects = projects.filter((item) => {
    const matchesStatus = statusFilter === "all" ? true : item.status === statusFilter;
    if (!matchesStatus) return false;
    if (!query) return true;
    const text = `${item.title} ${item.executiveSummary}`.toLowerCase();
    return text.includes(query);
  });

  async function updateStatus(projectId: number, status: "generated" | "archived") {
    setBusyId(projectId);
    setError("");
    try {
      const response = await fetch(`/api/plan/projects/${projectId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      const data = await response.json();
      if (!response.ok || !data.ok) {
        throw new Error(data.error || "Status konnte nicht aktualisiert werden.");
      }
      setProjects((prev) => prev.map((p) => (p.id === projectId ? { ...p, status } : p)));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unbekannter Fehler");
    } finally {
      setBusyId(null);
    }
  }

  async function deleteProject(projectId: number) {
    if (!window.confirm(`${t.confirmDelete} (#${projectId})`)) return;
    setBusyId(projectId);
    setError("");
    try {
      const response = await fetch(`/api/plan/projects/${projectId}`, { method: "DELETE" });
      const data = await response.json();
      if (!response.ok || !data.ok) {
        throw new Error(data.error || "Projekt konnte nicht geloescht werden.");
      }
      setProjects((prev) => prev.filter((p) => p.id !== projectId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unbekannter Fehler");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <main className="vtob-container">
      <div className="vtob-page-head">
        <div>
          <h1 style={{ marginBottom: 8 }}>{t.title}</h1>
          <p style={{ color: "#555" }}>{t.desc}</p>
        </div>
        <button className="vtob-btn" type="button" onClick={loadProjects} disabled={loading}>
          {t.refresh}
        </button>
      </div>

      {error ? <p style={{ color: "#b00020", marginBottom: 16 }}>{error}</p> : null}

      <section className="vtob-card" style={{ marginBottom: 16 }}>
        <div className="vtob-filter-grid">
          <label>
            {t.search}
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t.searchPh}
            />
          </label>
          <label>
            {t.filter}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as "all" | "generated" | "archived")}
            >
              <option value="all">{t.all}</option>
              <option value="generated">{t.generated}</option>
              <option value="archived">{t.archived}</option>
            </select>
          </label>
        </div>
      </section>

      {loading ? <p>{t.load}</p> : null}

      {!loading && visibleProjects.length === 0 ? <p>{t.empty}</p> : null}

      <section className="vtob-project-list">
        {visibleProjects.map((item) => (
          <article className="vtob-card" key={item.id}>
            <h3 style={{ marginBottom: 8 }}>{item.title}</h3>
            <p style={{ color: "#475569", marginBottom: 10 }}>
              {item.executiveSummary || "-"}
            </p>
            <div className="vtob-meta-grid">
              <span>
                {t.created}: <strong>{formatDate(item.createdAt, lang)}</strong>
              </span>
              <span>
                {t.version}: <strong>{item.versionNo}</strong>
              </span>
              <span>
                {t.status}: <strong>{statusLabel[item.status] || item.status}</strong>
              </span>
              <span>
                {t.source}: <strong>{item.provider}</strong>
              </span>
            </div>
            <div style={{ marginTop: 12 }}>
              <a className="vtob-link-btn" href={`/projekte/${item.id}`} style={{ marginRight: 8 }}>
                {t.open}
              </a>
              {item.status === "archived" ? (
                <button
                  className="vtob-link-btn"
                  type="button"
                  disabled={busyId === item.id}
                  onClick={() => updateStatus(item.id, "generated")}
                  style={{ marginRight: 8 }}
                >
                  {t.unarchive}
                </button>
              ) : (
                <button
                  className="vtob-link-btn"
                  type="button"
                  disabled={busyId === item.id}
                  onClick={() => updateStatus(item.id, "archived")}
                  style={{ marginRight: 8 }}
                >
                  {t.archive}
                </button>
              )}
              <button
                className="vtob-link-btn"
                type="button"
                disabled={busyId === item.id}
                onClick={() => deleteProject(item.id)}
              >
                {t.remove}
              </button>
            </div>
          </article>
        ))}
      </section>
    </main>
  );
}
